import { useMemo, useState } from "react";
import { useParams, useLocation } from "wouter";
import {
  useGetAccount,
  useListAccountMemberships,
  useListInventoryPlans,
  useGetAccountProforma,
  useGetMembershipPeriodLog,
  useUpdateAccountMembership,
  useReanchorAccountMembership,
  useToggleMembershipSundayAccess,
  getGetMembershipPeriodLogQueryKey,
  getGetAccountProformaQueryKey,
  getListAccountMembershipsQueryKey,
  getGetAccountPlanScheduleQueryKey,
  type Membership,
  type InventoryPlan,
  type MembershipPeriodLogEntry,
  type UpdateMembershipBodyCycleMode,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { computeQuotePricing, round2 } from "@workspace/billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";

type CycleMode = "Monthly" | "Quarterly" | "Six_Month" | "Yearly";

function formatINR(n: number): string {
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n)}`;
}

/**
 * Committed term in whole calendar months for an INCLUSIVE start→end window.
 * The end date is the last day of the term, so a period from the 1st to a
 * month's last day counts that whole month: Jul 1 → Dec 31 = 6 (not 5),
 * Jun 1 → Dec 31 = 7, Jun 1 → Jan 31 = 8. Always >= 1. Mirrors add-plan.tsx.
 */
function monthsBetween(startIso: string, endIso: string): number {
  if (!startIso || !endIso) return 1;
  const [sy, sm, sd] = startIso.split("-").map(Number);
  const [ey, em, ed] = endIso.split("-").map(Number);
  if (!sy || !ey) return 1;
  let months = (ey - sy) * 12 + (em - sm);
  if (ed >= sd) months += 1;
  return Math.max(1, months);
}

type Pricing = ReturnType<typeof computeQuotePricing>;

interface DealLine {
  membership: Membership;
  plan: InventoryPlan;
  isOneTime: boolean;
  period: number; // durationMonths (derived for One_Time)
  perSeatMonthly: number;
  lineTotal: number; // pricing.lines[0].lineTotal — rental incl GST, excludes deposit
  deposit: number; // this membership's held deposit
  pricing: Pricing;
}

/**
 * MODULE-SCOPE pure helper — mirrors the solo pricing memo's recipe exactly
 * (persisted membership values only, no editable state) so a deal's per-line
 * TOTAL matches what the solo View Plan would have shown for that same line.
 * Calls NO React hook — safe to `.map()` over the sibling array inside ONE
 * useMemo (see `dealPricing` below) without violating hooks rules.
 */
function computeDealLine(
  m: Membership,
  plans: InventoryPlan[],
  memberStateCode: string | null,
): DealLine | null {
  const plan = plans.find((p) => p.id === m.planId);
  if (!plan) return null;
  const monthlyBase = Number(plan.monthlyPrice);
  const lineDiscPct = Number(m.lineDiscountPct ?? 0);
  const discBase = monthlyBase * (1 - lineDiscPct / 100);
  const frozenStored = Number(m.frozenDiscountedPrice ?? m.unitPrice ?? monthlyBase);
  const derivedAddl =
    discBase > 0 ? Math.round((1 - frozenStored / discBase) * 10000) / 100 : 0;
  const isOneTime = (m.cycleMode as string | null | undefined) === "One_Time";
  const recurring = plan.cadence === "Recurring";
  const endDate = isOneTime
    ? (m.endDate ?? null)
    : recurring
      ? (m.status === "exited" && m.endDate ? m.endDate : null)
      : (m.endDate ?? null);
  const period = isOneTime
    ? monthsBetween(m.startDate ?? "", m.endDate ?? "")
    : Math.max(1, m.durationMonths ?? 1);
  const perSeatMonthly = Math.round(discBase * (1 - derivedAddl / 100) * 100) / 100;
  const pricing = computeQuotePricing({
    lines: [
      {
        planId: plan.id,
        description: plan.planName.replace(/_/g, " "),
        unitPrice: monthlyBase,
        qty: m.quantity ?? 1,
        durationMonths: period,
        lineDiscountPct: lineDiscPct,
        taxRatePct: plan.taxRatePct != null ? Number(plan.taxRatePct) : null,
        recurring,
        // Deposits are summed separately from securityDepositHeld — keep pricing's
        // grandTotal deposit-free so the combined GT reconciles with line TOTALs.
        securityDepositLineTotal: 0,
        // 0132 — without this the summary silently prices rent only, so a plan
        // WITH the add-on still showed its old Monthly (recurring) figure.
        sundayAccessMonthly: m.sundayAccess ? Number(m.sundayAccessRate ?? 0) : 0,
      },
    ],
    startDate: m.startDate ?? new Date().toISOString().slice(0, 10),
    endDate,
    billingCycleDay: m.billingCycleDay ?? 1,
    prorateFirstPeriod: false,
    additionalDiscountPct: derivedAddl,
    paymentMode: "Cash",
    gstApplicable: m.gstApplicable ?? true,
    issuerStateCode: "07",
    memberStateCode,
  });
  return {
    membership: m,
    plan,
    isOneTime,
    period,
    perSeatMonthly,
    lineTotal: pricing.lines[0]?.lineTotal ?? 0,
    deposit: Number(m.securityDepositHeld ?? 0),
    pricing,
  };
}

// View Plan — an Add-Plan-styled, read-only view of one existing plan
// (membership). Only End Date and Period (committed months) are editable;
// changes are recorded in a period-change log shown below.
export default function ViewPlanPage() {
  const { id, membershipId } = useParams<{ id: string; membershipId: string }>();
  const accountId = Number(id);
  const mId = Number(membershipId);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: account } = useGetAccount(accountId);
  const { data: memberships, isLoading } = useListAccountMemberships(accountId);
  const { data: plans = [] } = useListInventoryPlans();
  const update = useUpdateAccountMembership();
  // ANCHOR-05 — dedicated guarded sub-route, deliberately NOT part of `save()`
  // above (which never sends billingCycleDay — keep it that way; the anchor
  // DELETEs materialised period rows and needs its own confirm gate).
  const reanchorMutation = useReanchorAccountMembership();
  const sundayMutation = useToggleMembershipSundayAccess();

  const membership = (memberships as Membership[] | undefined)?.find((m) => m.id === mId) ?? null;
  const plan = (plans as InventoryPlan[]).find((p) => p.id === membership?.planId) ?? null;

  const { data: proforma } = useGetAccountProforma(
    accountId,
    membership?.proformaQuoteId ?? 0,
    {
      query: {
        enabled: !!membership?.proformaQuoteId,
        queryKey: getGetAccountProformaQueryKey(accountId, membership?.proformaQuoteId ?? 0),
      },
    },
  );
  const { data: periodLog = [] } = useGetMembershipPeriodLog(accountId, mId, {
    query: {
      enabled: Number.isFinite(mId),
      queryKey: getGetMembershipPeriodLogQueryKey(accountId, mId),
    },
  });

  // Editable state — seeded from the membership once loaded.
  const [ended, setEnded] = useState<boolean | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [period, setPeriod] = useState<number | null>(null);
  const [addlPct, setAddlPct] = useState<number | null>(null);
  const [cycleMode, setCycleMode] = useState<CycleMode | null>(null);
  // Phase 28: editable held deposit amount (UI28-02).
  // Seeded from membership.securityDepositHeld on first load, then from proforma fallback.
  const [heldDeposit, setHeldDeposit] = useState<number | null>(null);
  // ANCHOR-05 — billing-anchor re-anchor control. Separate from `save()`'s
  // dirty-tracking above; this fires its own guarded mutation via a confirm
  // dialog, not the page's regular Save.
  const [reanchorDay, setReanchorDay] = useState<number | null>(null);
  const [confirmReanchorOpen, setConfirmReanchorOpen] = useState(false);

  // The additional discount is per-membership: derive it from the frozen price
  // (monthly × (1−lineDisc) × (1−addl)) rather than the shared batch proforma, so
  // editing it here only affects THIS plan.
  const monthlyBase = plan ? Number(plan.monthlyPrice) : 0;
  const lineDiscPct = Number(membership?.lineDiscountPct ?? 0);
  const discBase = monthlyBase * (1 - lineDiscPct / 100);
  const frozenStored = Number(
    membership?.frozenDiscountedPrice ?? membership?.unitPrice ?? monthlyBase,
  );
  const derivedAddl = discBase > 0 ? Math.round((1 - frozenStored / discBase) * 10000) / 100 : 0;

  // One-time detection: the generated MembershipCycleMode type excludes
  // "One_Time" (only Add Plan's batch-line type includes it), so a widening
  // cast is required to read the runtime value without touching generated
  // files (CONTEXT LOCK: no server/generated-type changes).
  const isOneTime = (membership?.cycleMode as string | null | undefined) === "One_Time";

  // Effective values (state once touched, else the membership's current value).
  const effEnded = ended ?? membership?.status === "exited";
  const effEndDate = endDate ?? membership?.endDate ?? "";
  // One_Time: the operator edits End Date and Period DERIVES (inclusive
  // month count Start→End). Recurring/Day Pass: Period stays operator-edited.
  const derivedOneTimeMonths = monthsBetween(membership?.startDate ?? "", effEndDate || "");
  const effPeriod = isOneTime ? derivedOneTimeMonths : (period ?? membership?.durationMonths ?? 1);
  const effAddl = addlPct ?? derivedAddl;
  const effCycleMode: CycleMode =
    cycleMode ?? (membership?.cycleMode as CycleMode | null) ?? "Monthly";

  const recurring = plan?.cadence === "Recurring";

  const pricing = useMemo(() => {
    if (!membership || !plan) return null;
    return computeQuotePricing({
      lines: [
        {
          planId: plan.id,
          description: plan.planName.replace(/_/g, " "),
          unitPrice: Number(plan.monthlyPrice),
          qty: membership.quantity ?? 1,
          durationMonths: Math.max(1, effPeriod),
          lineDiscountPct: Number(membership.lineDiscountPct ?? 0),
          taxRatePct: plan.taxRatePct != null ? Number(plan.taxRatePct) : null,
          recurring,
          securityDepositLineTotal: Number(proforma?.securityDepositTotal ?? 0),
          sundayAccessMonthly: membership.sundayAccess
            ? Number(membership.sundayAccessRate ?? 0)
            : 0,
        },
      ],
      startDate: membership.startDate ?? new Date().toISOString().slice(0, 10),
      // One_Time always passes the term end (not gated on the "ended" toggle,
      // which is a recurring-only concept) so the engine takes the
      // `l.recurring && input.endDate` branch → endedRunTaxable (day-prorated
      // full term) and monthlyRecurringTotal stays 0. This matches how Add
      // Plan priced the one-time deal at creation.
      endDate: isOneTime ? (effEndDate || null) : (effEnded && effEndDate ? effEndDate : null),
      billingCycleDay: membership.billingCycleDay ?? 1,
      // View reflects actual recurring billing — no Add-Plan first-period stub.
      prorateFirstPeriod: false,
      additionalDiscountPct: effAddl,
      paymentMode: "Cash",
      gstApplicable: membership.gstApplicable ?? true,
      issuerStateCode: proforma?.issuerStateCode ?? "07",
      memberStateCode: proforma?.memberState ?? account?.placeOfSupply ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membership, plan, proforma, effPeriod, effEnded, effEndDate, effAddl, recurring, account, isOneTime]);

  // Deal detection (quick-11): a DEAL is a quoteGroupId shared by >=2
  // memberships. All hooks below stay ABOVE every early return (isLoading /
  // not-found / isDeal) so hook order is stable across renders.
  const quoteGroupId = membership?.quoteGroupId ?? null;
  const dealSiblings = useMemo(() => {
    if (!quoteGroupId) return [] as Membership[];
    return ((memberships as Membership[] | undefined) ?? [])
      .filter((m) => m.quoteGroupId === quoteGroupId)
      .sort((a, b) => a.id - b.id);
  }, [memberships, quoteGroupId]);
  const isDeal = quoteGroupId != null && dealSiblings.length >= 2;
  const dealPricing = useMemo(() => {
    if (!isDeal) return null;
    const lines = dealSiblings
      .map((m) => computeDealLine(m, plans as InventoryPlan[], account?.placeOfSupply ?? null))
      .filter((x): x is DealLine => x != null);
    const sum = (fn: (d: DealLine) => number) =>
      round2(lines.reduce((s, d) => s + fn(d), 0));
    const combined = {
      monthlyRecurringTotal: sum((d) => d.pricing.monthlyRecurringTotal),
      subtotal: sum((d) => d.pricing.subtotal),
      totalDiscount: sum((d) => d.pricing.totalDiscount),
      cgstTotal: sum((d) => d.pricing.cgstTotal),
      sgstTotal: sum((d) => d.pricing.sgstTotal),
      igstTotal: sum((d) => d.pricing.igstTotal),
      depositTotal: sum((d) => d.deposit),
      lineTotalSum: sum((d) => d.lineTotal),
    };
    return { lines, combined, grandTotal: round2(combined.lineTotalSum + combined.depositTotal) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeal, dealSiblings, plans, account]);

  // Phase 28: effective held deposit — operator state (if touched) else membership field, else proforma fallback.
  const depositProformaFallback = Number(proforma?.securityDepositTotal ?? 0);
  const seedDeposit = membership?.securityDepositHeld != null
    ? Number(membership.securityDepositHeld)
    : depositProformaFallback;
  const effHeldDeposit = heldDeposit ?? seedDeposit;

  // ANCHOR-05 — effective billing anchor (1..31) shown in the control below.
  // Clamped client-side only for display; the server re-validates.
  const seedBillingDay = membership?.billingCycleDay ?? 1;
  const effReanchorDay = reanchorDay ?? seedBillingDay;
  const reanchorDirty = recurring && !isOneTime && effReanchorDay !== seedBillingDay;

  // Grand total must reflect the ACTUAL held deposit shown on the line above, not
  // the proforma's original deposit estimate that computeQuotePricing baked into
  // pricing.securityDepositTotal. Swap the proforma deposit for effHeldDeposit so
  // the total reconciles with the displayed rows (rent + GST + held deposit).
  const effGrandTotal = pricing
    ? Math.round((pricing.grandTotal - pricing.securityDepositTotal + effHeldDeposit) * 100) / 100
    : 0;

  const dirty =
    membership != null &&
    (effEnded !== (membership.status === "exited") ||
      (effEndDate || null) !== (membership.endDate ?? null) ||
      effPeriod !== (membership.durationMonths ?? 1) ||
      effAddl !== derivedAddl ||
      (recurring && !isOneTime && effCycleMode !== ((membership.cycleMode as CycleMode | null) ?? "Monthly")) ||
      effHeldDeposit !== seedDeposit);

  async function save() {
    if (!membership) return;
    try {
      await update.mutateAsync({
        id: accountId,
        membershipId: mId,
        data: {
          durationMonths: Math.max(1, effPeriod),
          // One_Time: always send the term end. Recurring: an end date marks
          // the plan exited. Non-recurring (Day Pass): the end date is the
          // validity expiry (defaults to the start date) and stays active.
          endDate: isOneTime
            ? (effEndDate || membership.startDate || null)
            : recurring
              ? (effEnded && effEndDate ? effEndDate : null)
              : (effEndDate || membership.startDate || null),
          status: isOneTime
            ? "active"
            : recurring
              ? (effEnded && effEndDate ? "exited" : "active")
              : "active",
          additionalDiscountPct: effAddl,
          // Billing cadence is an explicit choice (recurring only). One_Time
          // must be PRESERVED (never nulled/coerced) — the generated
          // UpdateMembershipBodyCycleMode type excludes "One_Time" (server-side
          // accepts it), so a widening cast is required without touching
          // generated files (CONTEXT LOCK).
          cycleMode: isOneTime
            ? ("One_Time" as UpdateMembershipBodyCycleMode)
            : recurring
              ? effCycleMode
              : null,
          // Phase 28 (UI28-02): include held deposit in PATCH body.
          securityDepositHeld: effHeldDeposit,
        },
      });
      toast({ title: "Plan updated." });
      queryClient.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
      queryClient.invalidateQueries({ queryKey: getGetMembershipPeriodLogQueryKey(accountId, mId) });
      if (membership.proformaQuoteId) {
        queryClient.invalidateQueries({
          queryKey: getGetAccountProformaQueryKey(accountId, membership.proformaQuoteId),
        });
      }
      setEnded(null);
      setEndDate(null);
      setPeriod(null);
      setAddlPct(null);
      setCycleMode(null);
      setHeldDeposit(null);
    } catch {
      toast({ title: "Couldn't update the plan.", variant: "destructive" });
    }
  }

  // ANCHOR-05 — fires the guarded POST .../reanchor sub-route (never the
  // page's regular Save, which deliberately never sends billingCycleDay).
  // Surfaces a 422 (reanchor_blocked / reanchor_blocked_ledger /
  // reanchor_not_applicable) verbatim, naming the offending period ids —
  // a generic "Something went wrong" would hide money-relevant state.
  // 0132 — Sunday access is toggled through its OWN guarded sub-route, never via
  // save(). Same reasoning as reanchor: it rewrites rupee amounts on period rows
  // and must report which periods it touched and which it refused to.
  async function toggleSunday(next: boolean) {
    if (!membership) return;
    try {
      const result = await sundayMutation.mutateAsync({
        id: accountId,
        membershipId: mId,
        data: { enabled: next },
      });
      const skipped = result.skippedPeriodIds?.length ?? 0;
      const refresh = result.receiptsToRefreshPeriodIds?.length ?? 0;
      const alreadySent = result.receiptsAlreadySentPeriodIds?.length ?? 0;
      // A repriced period that already has an order receipt keeps the OLD figures
      // on that document until it is regenerated, so say so rather than leave the
      // operator to discover a stale receipt.
      const notes = [
        refresh > 0
          ? `${refresh} upcoming order receipt(s) already issued — open each and press Update to refresh it${
              alreadySent > 0 ? ` (${alreadySent} was already sent to the client at the old amount)` : ""
            }.`
          : null,
        skipped > 0 ? `${skipped} already-settled period(s) left unchanged.` : null,
      ].filter(Boolean);
      toast({
        title: next
          ? `Sunday access on${result.effectiveFrom ? ` from ${result.effectiveFrom}` : ""}.`
          : "Sunday access removed.",
        description: notes.length ? notes.join(" ") : undefined,
      });
      queryClient.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      toast({
        title: "Could not change Sunday access",
        description:
          code === "sunday_rate_not_configured"
            ? "No Sunday rate is set for this space type yet. Set it under Billing → Plans."
            : code === "sunday_access_not_applicable"
              ? "Sunday access is only sold on recurring cabin and workstation plans."
              : "Please try again.",
        variant: "destructive",
      });
    }
  }

  async function confirmReanchor() {
    if (!membership) return;
    try {
      const result = await reanchorMutation.mutateAsync({
        id: accountId,
        membershipId: mId,
        data: { billingCycleDay: effReanchorDay },
      });
      const deletedCount = result.deletedPeriodCount ?? 0;
      toast({
        title: `Billing day moved to ${result.billingCycleDay}.`,
        description:
          deletedCount > 0
            ? `${deletedCount} unpaid period(s) rebuilt against the new anchor.`
            : undefined,
      });
      queryClient.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      setReanchorDay(null);
    } catch (err) {
      const data = (err as { data?: { code?: string; periodIds?: number[] } })?.data;
      const code = data?.code;
      const periodIds = data?.periodIds ?? [];
      const description =
        code === "reanchor_blocked_ledger" && periodIds.length > 0
          ? `Periods #${periodIds.join(", #")} are still tied to a payment or credit note.`
          : code === "reanchor_blocked" && periodIds.length > 0
            ? `Periods #${periodIds.join(", #")} are already paid, part-paid, adjusted or receipted.`
            : code === "reanchor_not_applicable"
              ? "This plan doesn't have a recurring billing schedule to re-anchor."
              : undefined;
      toast({ title: "Can't re-anchor.", description, variant: "destructive" });
    } finally {
      setConfirmReanchorOpen(false);
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-1">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    );
  }
  if (!membership || !plan) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-center text-muted-foreground">
        Plan not found.{" "}
        <button className="text-primary underline" onClick={() => navigate(`/accounts/${accountId}`)}>
          Back to account
        </button>
      </div>
    );
  }

  // DEAL (quick-11): a quoteGroupId shared by >=2 memberships renders a
  // READ-ONLY multi-line viewer instead of the solo editable view below.
  // No editable inputs, no Status/Billing-Cycle/Discount/GST/Deposit
  // controls, no Save — deal terms change via Restructure or per-line.
  if (isDeal && dealPricing) {
    const { combined } = dealPricing;
    const intra = combined.igstTotal === 0;
    return (
      <div className="mx-auto max-w-3xl space-y-5 p-1">
        <button
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => navigate(`/accounts/${accountId}`)}
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div>
          <h1 className="text-2xl font-semibold">View Plan</h1>
          <p className="text-sm text-muted-foreground">
            {account?.legalName} — read-only combined-deal view. Deal terms change via Restructure or per-line.
          </p>
        </div>

        {/* Plan lines — one read-only row per sibling membership */}
        <div className="rounded-md border">
          <div className="grid grid-cols-[2fr_repeat(4,1fr)] items-center gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
            <span>SPACE TYPE</span>
            <span className="text-right">QTY</span>
            <span className="text-right">PERIOD</span>
            <span className="text-right">PER SEAT / MO</span>
            <span className="text-right">TOTAL</span>
          </div>
          {dealPricing.lines.map((d) => {
            const start = d.membership.startDate
              ? format(new Date(d.membership.startDate), "dd MMM yyyy")
              : null;
            const end = d.membership.endDate
              ? format(new Date(d.membership.endDate), "dd MMM yyyy")
              : null;
            const ended = d.isOneTime || (d.membership.status === "exited" && !!end);
            const dateNote = start
              ? ended && end
                ? ` · ${start} → ${end}`
                : ` · from ${start}`
              : "";
            return (
              <div
                key={d.membership.id}
                className="grid grid-cols-[2fr_repeat(4,1fr)] items-center gap-2 border-b px-3 py-3 last:border-b-0"
              >
                <div className="flex flex-col">
                  {/* Deal space-type line — show the SPACE TYPE only, no
                      individual "Plan #{id}" (this View is the deal; the id
                      belongs to the deal, not each space type). */}
                  <span className="text-sm font-medium">
                    {d.membership.quantity && d.membership.quantity > 1 ? `${d.membership.quantity} × ` : ""}
                    {d.plan.planName.replace(/_/g, " ")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {d.period} mo{dateNote}
                  </span>
                </div>
                <span className="text-right text-sm">{String(d.membership.quantity ?? 1)}</span>
                <span className="text-right text-sm">{d.period}</span>
                <span className="text-right text-sm">{formatINR(d.perSeatMonthly)}</span>
                <span className="text-right text-sm font-medium">{formatINR(d.lineTotal)}</span>
              </div>
            );
          })}
        </div>

        {/* Combined summary — reconciles with Σ per-line TOTAL + Σ held deposits */}
        <div className="ml-auto w-full max-w-sm rounded-md border bg-muted/20 p-4 text-sm">
          {combined.monthlyRecurringTotal > 0 && (
            <div className="mb-1 flex justify-between border-b pb-2 font-medium">
              <span>Monthly (recurring)</span>
              <span className="tabular-nums">{formatINR(combined.monthlyRecurringTotal)}</span>
            </div>
          )}
          <div className="flex justify-between py-0.5 text-muted-foreground">
            <span>Subtotal</span>
            <span className="tabular-nums">{formatINR(combined.subtotal)}</span>
          </div>
          {combined.totalDiscount > 0 && (
            <div className="flex justify-between py-0.5 text-muted-foreground">
              <span>Additional discount</span>
              <span className="tabular-nums">−{formatINR(combined.totalDiscount)}</span>
            </div>
          )}
          {intra && combined.cgstTotal + combined.sgstTotal > 0 && (
            <>
              <div className="flex justify-between py-0.5 text-muted-foreground">
                <span>CGST</span>
                <span className="tabular-nums">{formatINR(combined.cgstTotal)}</span>
              </div>
              <div className="flex justify-between py-0.5 text-muted-foreground">
                <span>SGST</span>
                <span className="tabular-nums">{formatINR(combined.sgstTotal)}</span>
              </div>
            </>
          )}
          {combined.igstTotal > 0 && (
            <div className="flex justify-between py-0.5 text-muted-foreground">
              <span>IGST</span>
              <span className="tabular-nums">{formatINR(combined.igstTotal)}</span>
            </div>
          )}
          {combined.depositTotal > 0 && (
            <div className="flex justify-between py-0.5 text-muted-foreground">
              <span>Security deposit (held)</span>
              <span className="tabular-nums">{formatINR(combined.depositTotal)}</span>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t pt-2 text-base font-semibold">
            <span>Grand Total</span>
            <span className="tabular-nums">{formatINR(dealPricing.grandTotal)}</span>
          </div>
        </div>

        {/* Actions — Back only; no Save for a read-only deal view */}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => navigate(`/accounts/${accountId}`)}>Back</Button>
        </div>
      </div>
    );
  }

  const intraState = pricing != null && pricing.igstTotal === 0;
  // Per-seat reflects the (possibly edited) discount live, not the stale frozen value.
  const perSeatMonthly = Math.round(discBase * (1 - effAddl / 100) * 100) / 100;

  // Read-only field wrapper.
  const RO = ({ label, value }: { label: string; value: string }) => (
    <div className="space-y-1">
      <Label className="text-muted-foreground">{label}</Label>
      <Input value={value} disabled className="disabled:opacity-100" />
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-1">
      <button
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        onClick={() => navigate(`/accounts/${accountId}`)}
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <h1 className="text-2xl font-semibold">View Plan</h1>
        <p className="text-sm text-muted-foreground">
          {isOneTime ? (
            <>
              {account?.legalName} — read-only; only{" "}
              <span className="font-medium">End Date</span>
              {seedDeposit > 0 ? (
                <>
                  {" "}and <span className="font-medium">Additional Discount</span> can be changed.
                </>
              ) : (
                <>
                  ,{" "}<span className="font-medium">Additional Discount</span> and{" "}
                  <span className="font-medium">Security Deposit (held)</span> can be changed.
                </>
              )}
            </>
          ) : (
            <>
              {account?.legalName} — read-only; only <span className="font-medium">End Date</span>,{" "}
              <span className="font-medium">Period</span>,{" "}
              <span className="font-medium">Billing Cycle</span>
              {seedDeposit > 0 ? (
                <>
                  {" "}and <span className="font-medium">Additional Discount</span> can be changed.
                </>
              ) : (
                <>
                  ,{" "}<span className="font-medium">Additional Discount</span> and{" "}
                  <span className="font-medium">Security Deposit (held)</span> can be changed.
                </>
              )}
            </>
          )}
        </p>
      </div>

      {/* Top: dates. Recurring → Start + Billing Day (editable, guarded
          re-anchor); One_Time → Start + an editable End Date (no billing
          day, no Status toggle); Non-recurring (Day Pass) → Start + an
          editable End Date (defaults to the start date, no billing day). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <RO label="Start Date" value={membership.startDate ? format(new Date(membership.startDate), "yyyy-MM-dd") : "—"} />
        {recurring && !isOneTime ? (
          <div className="space-y-1">
            <Label>Billing Day</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={31}
                value={effReanchorDay}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  // 2026-09-29: widened 28 → 31 alongside the server. Short
                  // months auto-cap at the target month's last day; the operator
                  // can pick 29/30/31 for members whose start-day is 29/30/31.
                  setReanchorDay(Math.min(31, Math.max(1, Number(e.target.value) || 1)))
                }
                className="h-9 w-20"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!reanchorDirty || reanchorMutation.isPending}
                onClick={() => setConfirmReanchorOpen(true)}
              >
                Re-anchor billing day
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Rebuilds the schedule from the plan&apos;s start date. Refused if any period is
              paid, part-paid, adjusted or receipted.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <Label>End Date</Label>
            <Input
              type="date"
              value={effEndDate || (membership.startDate ?? "")}
              min={membership.startDate ?? undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* One Time indicator — replaces the recurring Status/Billing Cycle
          block. No dropdown; Period derives from Start→End below. */}
      {isOneTime && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <RO label="Billing Cycle" value="One Time" />
        </div>
      )}

      {/* Status toggle — recurring plans only (excludes One_Time, which has
          no running/ended lifecycle; its validity is the start→end window
          above). */}
      {recurring && !isOneTime && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1">
            <Label className="block">Status</Label>
            <div className="flex h-9 items-center gap-2">
              <Switch
                checked={effEnded}
                onCheckedChange={(v) => {
                  setEnded(v);
                  if (v && !effEndDate) setEndDate(new Date().toISOString().slice(0, 10));
                }}
              />
              <span className="text-xs text-muted-foreground">{effEnded ? "Ended" : "Running"}</span>
            </div>
          </div>
          {/* Billing Cycle drives recurring billing only — an Ended plan has no
              recurring cycle, so the selector is hidden (and not required) when
              Ended; the existing cycle is preserved on save. */}
          {!effEnded && (
            <div className="space-y-1">
              <Label className="block">Billing Cycle</Label>
              <Select value={effCycleMode} onValueChange={(v) => setCycleMode(v as CycleMode)}>
                <SelectTrigger className="h-9 sm:w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                  <SelectItem value="Quarterly">Quarterly</SelectItem>
                  <SelectItem value="Six_Month">Half-yearly</SelectItem>
                  <SelectItem value="Yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {effEnded && (
            <div className="space-y-1">
              <Label>End Date</Label>
              <Input
                type="date"
                value={effEndDate}
                min={membership.startDate ?? undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="sm:w-[220px]"
              />
            </div>
          )}
        </div>
      )}

      {/* Plan line */}
      <div className="rounded-md border">
        <div className="grid grid-cols-[2fr_repeat(4,1fr)] items-center gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
          <span>SPACE TYPE</span>
          <span className="text-right">QTY</span>
          <span className="text-right">PERIOD</span>
          <span className="text-right">PER SEAT / MO</span>
          <span className="text-right">TOTAL</span>
        </div>
        <div className="grid grid-cols-[2fr_repeat(4,1fr)] items-center gap-2 px-3 py-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium">Plan #{membership.id}</span>
            <span className="text-xs text-muted-foreground">
              {membership.quantity && membership.quantity > 1 ? `${membership.quantity}× ` : ""}
              {plan.planName.replace(/_/g, " ")} · {effPeriod} mo
            </span>
          </div>
          <Input value={String(membership.quantity ?? 1)} disabled className="h-9 px-1.5 text-right disabled:opacity-100" />
          {isOneTime ? (
            <Input
              value={String(effPeriod)}
              disabled
              className="h-9 px-1.5 text-right disabled:opacity-100"
            />
          ) : (
            <Input
              type="number"
              min={1}
              value={effPeriod}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setPeriod(Math.max(1, Number(e.target.value) || 1))}
              className="h-9 px-1.5 text-right"
            />
          )}
          <span className="text-right text-sm">{formatINR(perSeatMonthly)}</span>
          <span className="text-right text-sm font-medium">
            {pricing ? formatINR(pricing.lines[0]?.lineTotal ?? 0) : "—"}
          </span>
        </div>
      </div>

      {/* Deal controls — additional discount is editable (per-plan). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label className="block">Additional Discount %</Label>
          <Input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={effAddl}
            onChange={(e) =>
              setAddlPct(Math.min(100, Math.max(0, Number(e.target.value) || 0)))
            }
          />
        </div>
        <div className="space-y-1">
          <Label className="block">Charge GST</Label>
          <div className="flex h-9 items-center gap-2">
            <Switch checked={!!membership.gstApplicable} disabled />
            <span className="text-xs text-muted-foreground">{membership.gstApplicable ? "Included" : "Excluded"}</span>
          </div>
        </div>
        {/* 0132 — Sunday access. Its own mutation, deliberately NOT part of the
            Save button's dirty set: it reprices period rows immediately and
            reports what it skipped, which a bulk save cannot convey. */}
        <div className="space-y-1">
          <Label className="block">Sunday Access</Label>
          <div className="flex h-9 items-center gap-2">
            <Switch
              checked={!!membership.sundayAccess}
              disabled={sundayMutation.isPending || isDeal}
              onCheckedChange={(v) => void toggleSunday(v)}
            />
            <span className="text-xs text-muted-foreground">
              {membership.sundayAccess
                ? `+${formatINR(Number(membership.sundayAccessRate ?? 0) * (membership.quantity ?? 1))}/mo`
                : "Not included"}
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Applies from the next unpaid period. Paid or receipted periods are
            never changed. Billing only — it does not unlock the door.
          </p>
        </div>
        {/* Phase 28 UI28-02: editable held deposit, visibly separate from rent summary.
            2026-06-29: once the persisted deposit is non-zero (collected and Held),
            the input is locked — accidental edits to a held amount would silently
            change financial state. Release / refund happens through a separate
            flow (not in scope here). The input is only editable when the
            persisted value is 0 (first-time entry). */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label className="block">Security Deposit (held)</Label>
            {effHeldDeposit > 0 && (
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                Held
              </span>
            )}
          </div>
          <Input
            type="number"
            min={0}
            value={effHeldDeposit}
            onChange={(e) =>
              setHeldDeposit(Math.max(0, Number(e.target.value) || 0))
            }
            className="h-9"
            readOnly={seedDeposit > 0}
            disabled={seedDeposit > 0}
          />
          <p className="text-[10px] text-muted-foreground">
            {seedDeposit > 0
              ? "Held amount is locked. Release or refund through the deposit settlement flow."
              : "One-time refundable amount held; not affected by discounts."}
          </p>
        </div>
      </div>

      {/* Summary */}
      {pricing && (
        <div className="ml-auto w-full max-w-sm rounded-md border bg-muted/20 p-4 text-sm">
          {pricing.monthlyRecurringTotal > 0 && (
            <div className="mb-1 flex justify-between border-b pb-2 font-medium">
              <span>Monthly (recurring)</span>
              <span className="tabular-nums">{formatINR(pricing.monthlyRecurringTotal)}</span>
            </div>
          )}
          <div className="flex justify-between py-0.5 text-muted-foreground">
            <span>Subtotal</span>
            <span className="tabular-nums">{formatINR(pricing.subtotal)}</span>
          </div>
          {pricing.totalDiscount > 0 && (
            <div className="flex justify-between py-0.5 text-muted-foreground">
              <span>Additional discount</span>
              <span className="tabular-nums">−{formatINR(pricing.totalDiscount)}</span>
            </div>
          )}
          {/* 0132 — shown below the discount because the fee is deliberately not
              reduced by it. Subtotal above is rent only. */}
          {pricing.sundayAccessTotal > 0 && (
            <div className="flex justify-between py-0.5 text-muted-foreground">
              <span>Sunday access</span>
              <span className="tabular-nums">{formatINR(pricing.sundayAccessTotal)}</span>
            </div>
          )}
          {membership.gstApplicable && intraState && (
            <>
              <div className="flex justify-between py-0.5 text-muted-foreground">
                <span>CGST</span>
                <span className="tabular-nums">{formatINR(pricing.cgstTotal)}</span>
              </div>
              <div className="flex justify-between py-0.5 text-muted-foreground">
                <span>SGST</span>
                <span className="tabular-nums">{formatINR(pricing.sgstTotal)}</span>
              </div>
            </>
          )}
          {membership.gstApplicable && !intraState && (
            <div className="flex justify-between py-0.5 text-muted-foreground">
              <span>IGST</span>
              <span className="tabular-nums">{formatINR(pricing.igstTotal)}</span>
            </div>
          )}
          {effHeldDeposit > 0 && (
            <div className="flex justify-between py-0.5 text-muted-foreground">
              <span>Security deposit (held)</span>
              <span className="tabular-nums">{formatINR(effHeldDeposit)}</span>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t pt-2 text-base font-semibold">
            <span>Grand Total</span>
            <span className="tabular-nums">{formatINR(effGrandTotal)}</span>
          </div>
        </div>
      )}

      {/* Change history */}
      <div className="rounded-md border">
        <div className="border-b px-4 py-2.5">
          <h3 className="text-sm font-medium">Change history</h3>
          <p className="text-xs text-muted-foreground">Period &amp; end-date changes for this plan.</p>
        </div>
        {(periodLog as MembershipPeriodLogEntry[]).length === 0 ? (
          <p className="px-4 py-5 text-center text-sm text-muted-foreground">No changes recorded yet.</p>
        ) : (
          <ul className="divide-y">
            {(periodLog as MembershipPeriodLogEntry[]).map((e) => {
              const isPeriod = e.field === "period_months";
              const isDiscount = e.field === "additional_discount_pct";
              // Phase 28 LOG28-01: new change-history field labels.
              // period_amount_override is logged when a period is pinned (amountOverridden=true).
              const isDeposit = e.field === "security_deposit_held";
              const isPeriodAmount = e.field === "period_amount_override";
              // Note: PERIOD28-05 Edit-amount button guard (amountOverridden) lives in
              // account-detail.tsx PlanSchedule component alongside the period table.
              // 0132 — logged as the per-unit monthly rate ("0" when switched off),
              // so it renders through the existing currency formatter.
              const isSunday = e.field === "sunday_access";
              const isCurrency = isDeposit || isPeriodAmount || isSunday;
              const numeric = isPeriod || isDiscount || isCurrency;
              const label = isPeriod
                ? "Period"
                : isDiscount
                  ? "Discount"
                  : isDeposit
                    ? "Security Deposit (held)"
                    : isPeriodAmount
                      ? "Period Amount"
                      : isSunday
                        ? "Sunday Access"
                        : "End Date";
              const oldN = Number(e.oldValue ?? 0);
              const newN = Number(e.newValue ?? 0);
              const dir = numeric ? (newN > oldN ? "increased" : newN < oldN ? "decreased" : "changed") : "changed";
              const fmt = (v: string | null | undefined) =>
                v == null || v === ""
                  ? "—"
                  : isPeriod
                    ? `${v} mo`
                    : isDiscount
                      ? `${v}%`
                      : isCurrency
                        ? formatINR(Number(v))
                        : format(new Date(v), "dd MMM yyyy");
              return (
                <li key={e.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                  <div>
                    <span className="font-medium">{label}</span>{" "}
                    <Badge variant="outline" className="ml-1 text-[10px] capitalize">{dir}</Badge>
                    <div className="text-xs text-muted-foreground">
                      {fmt(e.oldValue)} → {fmt(e.newValue)}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(e.changedAt), "dd MMM yyyy, h:mm a")}
                    {e.changedBy ? ` · User #${e.changedBy}` : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={() => navigate(`/accounts/${accountId}`)}>Back</Button>
        <Button onClick={save} disabled={!dirty || update.isPending}>
          {update.isPending ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      {/* ANCHOR-05 — re-anchor confirmation. This DELETEs materialised period
          rows (guarded server-side); the operator must explicitly confirm. */}
      <AlertDialog
        open={confirmReanchorOpen}
        onOpenChange={(o) => {
          if (!o) setConfirmReanchorOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-anchor billing day to {effReanchorDay}?</AlertDialogTitle>
            <AlertDialogDescription>
              Re-anchoring rebuilds this plan&apos;s billing schedule from its start date. It is
              refused if any period has been paid, part-paid, amount-adjusted or receipted.
              Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReanchor} disabled={reanchorMutation.isPending}>
              {reanchorMutation.isPending ? "Re-anchoring…" : "Re-anchor"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
